import { useEffect, useMemo, useState } from "react";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import Message from "../../components/Message";
import { getAllLocations, getAllStoreProducts, getAllStoreSkus, getCategoriesByStoreId } from "../../controllers/CatalogController";
import { flattenLocationStores } from "../../utils/locations";
import {
    deleteFlashSaleDiscount,
    getFlashSaleDiscounts,
    saveFlashSaleDiscount,
    updateFlashSaleDiscount,
} from "../../controllers/FlashSaleDiscountController";
import DiscountFormContainer from "./components/DiscountForm";
import DiscountSearchContainer from "./components/DiscountSearch";
import DiscountTableContainer from "./components/DiscountList";

const EMPTY_FILTERS = {
    storeId: "",
    entityType: "",
    entityId: "",
};

const EMPTY_FORM = {
    storeId: "",
    entityType: "",
    entityId: "",
    discountPct: "",
};

export default function DiscountContainer() {
    const [rows, setRows] = useState([]);
    const [stores, setStores] = useState([]);
    const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [lastSearch, setLastSearch] = useState(null);
    const [apiMessage, setApiMessage] = useState({
        open: false,
        type: "success",
        message: "",
    });
    const [entityOptions, setEntityOptions] = useState([]);
    const [searchEntityOptions, setSearchEntityOptions] = useState([]);

    const storeName = useMemo(() => {
        return stores.find((store) => String(store.storeId) === String(filters.storeId))?.storeName || "";
    }, [filters.storeId]);

    useEffect(() => {
        fetchStores();
    }, []);

    useEffect(() => {
        if (!filters.storeId) {
            setRows([]);
            setLastSearch(null);
            setPage(0);
            return;
        }
        if (filters.entityType) {
            getEntities();
        }
    }, [filters.storeId, filters.entityType]);

    useEffect(() => {
        if (!form.storeId) {
            return;
        }
        if (form.entityType) {
            getEntities(false);
        }
    }, [form.storeId, form.entityType]);

    const getEntities = async (search = true) => {

        if (search) {
            switch (filters.entityType) {
                case "CATEGORY":
                    getStoreCategories(filters.storeId);
                    break;
                case "PRODUCT":
                    getStoreProducts(filters.storeId);
                    break;
                case "SKU":
                    getStoreSkus(filters.storeId);
                    break;
                default:
                    setSearchEntityOptions([]);
            }
        } else {
            switch (form.entityType) {
                case "CATEGORY":
                    getStoreCategories(form.storeId, false);
                    break;
                case "PRODUCT":
                    getStoreProducts(form.storeId, false);
                    break;
                case "SKU":
                    getStoreSkus(form.storeId, false);
                    break;
                default:
                    setEntityOptions([]);
            }
        }
    }

    const getStoreCategories = async (storeId, search = true) => {
        try {
            const response = await getCategoriesByStoreId(storeId);
            if (search) {
                setSearchEntityOptions(response);
            } else {
                setEntityOptions(response);
            }
        } catch (error) {
            if (search) {
                setSearchEntityOptions([]);
            } else {
                setEntityOptions([]);
            }
            setApiMessage({
                open: true,
                type: "error",
                message: error && error.message ? error.message : "Failed to load categories",
            });
        }
    }

    const getStoreSkus = async (storeId, search = true) => {
        try {
            const response = await getAllStoreSkus(storeId);
            const mappedOptions = response.map((option) => ({
                ...option,
                id: option.productItemId
            }));
            if (search) {
                setSearchEntityOptions(mappedOptions);
            } else {
                setEntityOptions(mappedOptions);
            }
        } catch (error) {
            if (search) {
                setSearchEntityOptions([]);
            } else {
                setEntityOptions([]);
            }
            setApiMessage({
                open: true,
                type: "error",
                message: error && error.message ? error.message : "Failed to load SKUs",
            });
        }
    }

    const getStoreProducts = async (storeId, search = true) => {
        try {
            const response = await getAllStoreProducts(storeId);
            if (search) {
                setSearchEntityOptions(response);
            } else {
                setEntityOptions(response);
            }
        } catch (error) {
            if (search) {
                setSearchEntityOptions([]);
            } else {
                setEntityOptions([]);
            }
            setApiMessage({
                open: true,
                type: "error",
                message: error && error.message ? error.message : "Failed to load products",
            });
        }
    }

    const fetchStores = async () => {
        try {
            const response = await getAllLocations();
            setStores(flattenLocationStores(response, { withCatalogId: false }));
        } catch (error) {
            setApiMessage({
                open: true,
                type: "error",
                message: error && error.message ? error.message : "Failed to load stores",
            });
        }
    };

    const fetchDiscounts = async (searchFilters) => {
        try {
            setLoading(true);
            const response = await getFlashSaleDiscounts(searchFilters);
            setRows(response);
            setPage(0);
        } catch (error) {
            setRows([]);
            setApiMessage({
                open: true,
                type: "error",
                message: error && error.message ? error.message : "Failed to load discounts",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            entityId: field === "entityType" || field === "storeId" ? "" : prev.entityId,
            [field]: value,
        }));
    };

    const handleFormChange = (field, value) => {
        setForm((prev) => ({
            ...prev,
            entityId: field === "entityType" || field === "storeId" ? "" : prev.entityId,
            [field]: value,
        }));
    };

    const handleSearch = async () => {
        if (!filters.storeId) {
            setApiMessage({
                open: true,
                type: "error",
                message: "Store is required to fetch discounts",
            });
            return;
        }
        const nextFilters = { ...filters };
        setLastSearch(nextFilters);
        await fetchDiscounts(nextFilters);
    };

    const handleClearSearch = () => {
        setFilters({ ...EMPTY_FILTERS });
        setRows([]);
        setLastSearch(null);
        setPage(0);
    };

    const resetForm = () => {
        setForm({ ...EMPTY_FORM });
        setIsEditMode(false);
    };

    const openCreateModal = () => {
        resetForm();
        setIsFormOpen(true);
    };

    const closeFormModal = () => {
        setIsFormOpen(false);
    };

    const handleEdit = (row) => {
        setForm({
            id: row.id,
            storeId: String(row.storeId ?? ""),
            entityType: row.entityType ?? "",
            entityId: String(row.entityId ?? ""),
            discountPct: String(row.discountPct ?? ""),
        });
        setIsEditMode(true);
        setIsFormOpen(true);
    };

    const handleSave = async () => {
        if (!form.storeId || !form.entityType || !form.entityId) {
            setApiMessage({
                open: true,
                type: "error",
                message: "Store, Entity Type, and Entity ID are required",
            });
            return;
        }

        const discountValue = Number(form.discountPct);
        if (Number.isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
            setApiMessage({
                open: true,
                type: "error",
                message: "Discount % must be between 0 and 100",
            });
            return;
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updateFlashSaleDiscount(form.id, form);
            } else {
                await saveFlashSaleDiscount(form);
            }

            setApiMessage({
                open: true,
                type: "success",
                message: isEditMode ? "Discount updated successfully" : "Discount saved successfully",
            });

            closeFormModal();
            resetForm();

            const refreshFilters =
                lastSearch && String(lastSearch.storeId) === String(form.storeId)
                    ? { ...lastSearch }
                    : {
                        storeId: String(form.storeId),
                        entityType: "",
                        entityId: "",
                    };

            setFilters(refreshFilters);
            setLastSearch(refreshFilters);
            await fetchDiscounts(refreshFilters);
        } catch (error) {
            setApiMessage({
                open: true,
                type: "error",
                message: error && error.message ? error.message : "Failed to save discount",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await deleteFlashSaleDiscount(id);
            setApiMessage({
                open: true,
                type: "success",
                message: "Discount deleted successfully",
            });
            await fetchDiscounts(lastSearch);
        } catch (error) {
            setApiMessage({
                open: true,
                type: "error",
                message: error && error.message ? error.message : "Failed to delete discount",
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Box sx={{ px: 1, pb: 3 }}>
            <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Message
                open={apiMessage.open}
                type={apiMessage.type}
                message={apiMessage.message}
                onClose={() => setApiMessage((prev) => ({ ...prev, open: false }))}
            />

            <DiscountFormContainer
                open={isFormOpen}
                form={form}
                stores={stores}
                isEditMode={isEditMode}
                onChange={handleFormChange}
                onSave={handleSave}
                onReset={resetForm}
                onClose={closeFormModal}
                entityOptions={entityOptions}
                loading={loading}
            />

            <DiscountSearchContainer
                filters={filters}
                stores={stores}
                onChange={handleFilterChange}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                onCreateDiscount={openCreateModal}
                entityOptions={searchEntityOptions}
                loading={loading}
            />

            <DiscountTableContainer
                rows={rows}
                page={page}
                size={size}
                loading={loading}
                hasSearched={Boolean(lastSearch)}
                onPageChange={setPage}
                onRowsPerPageChange={(nextSize) => {
                    setSize(nextSize);
                    setPage(0);
                }}
                onEdit={handleEdit}
                storeName={storeName}
                onDelete={handleDelete}
            />
        </Box>
    );
}